import {
  ContentServiceHttpClient,
  StructuredAnalysisHttpClient,
  ProjectHttpClient,
  ReportingApiClient,
} from '../../services/httpClient';
import {
  structuredAnalysisDocumentsPath,
  getDocumentsWithDataPath,
  getAllLatestDocumentsWithContent,
} from '../../services/urlService';

export const mapAnalysisToApi = (analysis) => ({
  ...analysis,
  body: analysis.body || null,
  source: analysis.source || '',
  tags: analysis.tags.map((tag) => ({
    text: tag,
  })),
});

const baseStructuredAnalysisDocumentsPath = (projectId) =>
  `projects/${projectId}/structuredAnalysisDocuments`;

const baseStructuredAnalysesServicePath = (projectId, analysisThreadId) =>
  `projects/${projectId}/structuredanalyses/${analysisThreadId || ''}`;

export const getAllStructuredIngestionDocuments = async (projectId) => {
  const response = await StructuredAnalysisHttpClient.get(
    structuredAnalysisDocumentsPath(projectId)
  );
  return response.data;
};

export const getStructuredAnalysisVersions = async (
  projectId,
  analysisThreadId
) => {
  try {
    const response = await ProjectHttpClient.get(
      `${baseStructuredAnalysesServicePath(
        projectId,
        analysisThreadId
      )}/versions`
    );

    return {
      projectId,
      analysisThreadId,
      analysisId: analysisThreadId,
      data: response.data,
    };
  } catch (error) {
    return {
      projectId,
      analysisThreadId,
      analysisId: analysisThreadId,
      data: [],
    };
  }
};

export const postIngestStructuredDocument = async (
  projectId,
  documentCodes /** an array of strings */
) => {
  const response = await StructuredAnalysisHttpClient.post(
    baseStructuredAnalysisDocumentsPath(projectId),
    {
      documentCodes,
    }
  );
  return response.data;
};

export const getStructuredAnalysisDocumentsWithData = async (projectId) => {
  const response = await StructuredAnalysisHttpClient.get(
    getDocumentsWithDataPath(projectId)
  );
  return response.data;
};

export const getAllDocumentsWithContentForProjectAndViewingType = async (
  projectId,
  isViewingAsClient
) => {
  const url = getAllLatestDocumentsWithContent(projectId, isViewingAsClient);
  const response = await StructuredAnalysisHttpClient.get(url);
  return response.data;
};

export const getLibraryItemLinksByContentVersion = async (
  projectId,
  documentCode,
  contentThreadId,
  contentVersionNumber,
  isViewingAsClient
) => {
  const endpoint = `${baseStructuredAnalysisDocumentsPath(
    projectId
  )}/libraryItemsForContent?documentCode=${documentCode}&contentThreadId=${contentThreadId}&contentVersionNumber=${contentVersionNumber}&isViewingAsClient=${isViewingAsClient}`;
  const response = await StructuredAnalysisHttpClient.get(endpoint);
  return response.data.libraryItemLinks;
};

export const putStructuredAnalyses = async (projectId, analysis) => {
  const response = await ContentServiceHttpClient.put(
    `/projects/${projectId}/StructuredAnalyses/${analysis.contentId}`,
    mapAnalysisToApi(analysis)
  );

  return response.data;
};

export const deleteStructuredDocument = async (projectId, documentCode) => {
  const response = await StructuredAnalysisHttpClient.delete(
    `${baseStructuredAnalysisDocumentsPath(projectId)}`,
    { documentCode }
  );

  return response;
};

/**
 * @deprecated ? should be migrated on BE to contentApi (/contentapi/v1/projects/{projectId}/Analyses)
 */
export const getStructuredAnalyses = async (projectId) => {
  const response = await ProjectHttpClient.get(
    `projects/${projectId}/StructuredAnalyses`
  );
  return response.data;
};

export const searchAnalyses = async (projectId, analysisType) => {
  const response = await StructuredAnalysisHttpClient.get(
    `${baseStructuredAnalysesServicePath(projectId)}search?type=${analysisType}`
  );
  return response.data;
};

export const updateStructuredAnalysisStatus = async (
  projectId,
  analysisThreadId,
  workflowAction
) => {
  const url = `${baseStructuredAnalysesServicePath(
    projectId,
    analysisThreadId
  )}/${workflowAction}`;
  return StructuredAnalysisHttpClient.post(url);
};

export const getStructuredAnalysis = async (
  projectId,
  analysisThreadId,
  versionStatus
) => {
  const response = await ProjectHttpClient.get(
    baseStructuredAnalysesServicePath(projectId, analysisThreadId) +
      (versionStatus ? `/${versionStatus}` : '')
  );
  return response.data;
};

/**
 * Retreives the structured analysis table download password (required for non US projects)
 */
export const getStructuredAnalysisPassword = async (
  projectId,
  contentThreadId,
  version
) => {
  const response = await ReportingApiClient.get(
    `/projects/${projectId}/content/${contentThreadId}/${version}/password`
  );
  return response.data;
};
